import { useEffect, useState } from "react";
import { MapPin, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Slim sticky banner at the very top of the page announcing which cities
 * Tent N Tap currently services. Fully admin-editable (Admin → Service Areas →
 * "Announcement banner" section) via /api/settings/service-announcement.
 * Dismissible per browser session — the message reappears in the next visit
 * so we don't hide critical coverage info permanently.
 */
export default function ServiceAnnouncementBar() {
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return sessionStorage.getItem("tnt_service_ann_dismissed") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    api.get("/settings/service-announcement")
      .then((r) => setAnn(r.data))
      .catch(() => {});
  }, []);

  if (!ann || !ann.enabled || dismissed) return null;
  const title = ann.banner_title || "";
  const text = ann.banner_text || (Array.isArray(ann.cities) && ann.cities.length ? ann.cities.join(" · ") : "");
  if (!title && !text) return null;

  return (
    <div
      data-testid="service-announcement-bar"
      className="sticky top-0 z-40 bg-gradient-to-r from-[#292724] via-[#3a2f24] to-[#292724] text-white border-b border-[#d4af37]/40 shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3">
        <MapPin className="w-4 h-4 text-[#d4af37] shrink-0" />
        <div className="flex-1 min-w-0 text-[12px] sm:text-[13px] leading-snug">
          {title && <span data-testid="service-ann-title" className="font-serif text-[#d4af37] mr-2">{title}</span>}
          {text && <span data-testid="service-ann-text" className="text-white/85">{text}</span>}
        </div>
        <button
          data-testid="service-ann-dismiss"
          onClick={() => {
            setDismissed(true);
            try { sessionStorage.setItem("tnt_service_ann_dismissed", "1"); } catch { /* noop */ }
          }}
          aria-label="Dismiss announcement"
          className="relative tap-target w-6 h-6 rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
